import _ from 'lodash'
import {combineEpics, Epic} from 'redux-observable'
import {from, merge, of} from 'rxjs'
import {catchError, filter, map, mergeMap} from 'rxjs/operators'
import axios from 'axios'
import moment from 'moment'
import {bsFetchCollectionAction, bsMergeResponseAction, bsRefreshCollectionAction, bsSkipFetchCollectionAction} from 'module/BSCollection/action/BSCollectionAction'
import {BSCollectionDict} from 'module/BSCollection/dict/BSCollectionDict'
import {genMomentRangeArray} from 'util/DateTimeUtil';
import {getSisDateAsISO, getSisDateAsNumber} from 'util/SisDateTime';
import {BSCollectionUrlParamSelectorDict} from 'module/BSCollection/dict/BSCollectionUrlParamSelectorDict';
import {ajaxStatusDoneAction, ajaxStatusErrorAction, ajaxStatusStartAction} from 'module/AjaxStatus/action/AjaxStatusAction';
import {parseServerError} from 'module/Error/util/parseServerError';
import {BSModelIDType, BSModelType, BSOwner, BSOwnerDateFetchTimestamps} from 'module/BSCollection/type/BSCollectionStateType';
import {SisNameBS} from 'module/BSCollection/type/SisNameBS';
import {alertError, alertServerError} from 'module/ConfirmDialog/component/ConfirmDialogContainer'

interface WorkingRequest {
	sisName: SisNameBS
	owners: BSOwner[]
	start: number
	end: number
	request: Promise<BSModelType[]>
}

let workingRequests: WorkingRequest[] = [];
const getCurrentRequest = (sisName: SisNameBS, owners: BSOwner[], start: number, end: number) =>
	workingRequests.find(
		r => r.sisName === sisName && r.start <= start && r.end >= end && _.isEqual(r.owners, owners),
	);
const addRequest = (sisName: SisNameBS, owners: BSOwner[], start: number, end: number, request: Promise<BSModelType[]>) => workingRequests.push({sisName, owners, start, end, request});

const removeRequest = (sisName: SisNameBS, owners: BSOwner[], start: number, end: number) => _.remove(workingRequests, {sisName, owners, start, end});


function isUpToDate<ID extends BSModelIDType>(msInterval: number, ownerDateFetchTimestamps: BSOwnerDateFetchTimestamps, owners: BSOwner[], fetchDates: number[]) {
	if (!fetchDates || !fetchDates.length || !owners || !owners.length) {
		return true
	}
	if (!ownerDateFetchTimestamps) return false
	const validUntilTS = +moment().subtract(msInterval, 'ms');
	return owners.every(v => {
		const od = ownerDateFetchTimestamps[v];
		if (!od) return false;
		return fetchDates.every(date => {
			const cachedTS = od[date];
			return cachedTS && cachedTS > validUntilTS;
		})
	});
}

const OWNER_LIMIT = 200;
const RefreshBSCollectionEpic: Epic = (action$, state$) => action$.pipe(
	filter(bsRefreshCollectionAction.match),
	map(action => {
		const {sisName, from, to, force, overrideOwners} = action.payload;
		const state = state$.value;
		const owners = overrideOwners ? overrideOwners : BSCollectionUrlParamSelectorDict[sisName](state);
		if (!sisName || !owners || !owners.length || !from || !to) {
			return bsSkipFetchCollectionAction(action.payload);
		}
		const {fetchBoundary, fetchExpand, interval, indexScope, ownerPluralLabel} = BSCollectionDict[sisName];
		if (owners.length > OWNER_LIMIT) {
			void alertError(`Zu viele ${ownerPluralLabel} aktiv. Passen Sie die Anzahl in den Anzeige-Einstellungen an, damit nicht mehr als ${OWNER_LIMIT} aktiv sind. \n\nDie Daten werden nicht aktualisiert.`)
			return bsSkipFetchCollectionAction(action.payload);
		}
		const startM = moment(from).startOf(fetchBoundary).subtract(fetchExpand, 'ms')
		const endM = moment(to).endOf(fetchBoundary).add(fetchExpand, 'ms')
		const startTS = +startM
		const endTS = +endM
		const start = getSisDateAsNumber(startM);
		const end = getSisDateAsNumber(endM);
		const currentReq = getCurrentRequest(sisName, owners, start, end);
		if (!force && currentReq) {
			return bsSkipFetchCollectionAction(action.payload);
		}
		const fetchDates = genMomentRangeArray(startM, endM, indexScope).map(m => getSisDateAsNumber(m));
		const bsCollection = state.BSCollection[sisName] || {}
		if (!force && isUpToDate(interval, bsCollection.ownerDateFetchTimestamps, owners, fetchDates)) {
			return bsSkipFetchCollectionAction(action.payload);
		}
		return bsFetchCollectionAction({sisName, owners, startTS, endTS, fetchDates});
	}),
)


const FetchBSCollectionEpic: Epic = (action$, state$) => action$.pipe(
	filter(bsFetchCollectionAction.match),
	mergeMap(action => {
		let {sisName, owners, startTS, endTS, fetchDates} = action.payload;
		const start = getSisDateAsNumber(moment(startTS))
		const end = getSisDateAsNumber(moment(endTS))
		const {url, dateScopeOnly, ownerUrlParam, label} = BSCollectionDict[sisName];
		if (dateScopeOnly) owners = [''];
		const params = {
			start,
			end,
			[ownerUrlParam]: owners,
		};
		const request = axios.get<BSModelType[]>(url, {params})
			//ensure sisName is set
			.then(({data}) => data.map(x => ({...x, sisName} as BSModelType)));
		addRequest(sisName, owners, start, end, request);
		const ts = +moment()
		return merge(
			of(ajaxStatusStartAction({name: sisName, ts, info: [label, `von ${getSisDateAsISO(start)} bis ${getSisDateAsISO(end)}`]})),
			from(request).pipe(
				mergeMap(models => {
					// noinspection JSCheckFunctionSignatures
					removeRequest(sisName, owners, start, end);
					return [
						ajaxStatusDoneAction({name: sisName, ts}),
						bsMergeResponseAction({data: models, sisName, owners, startTS, endTS, fetchDates}),
					]
				}),
				catchError(error => {
					removeRequest(sisName, owners, start, end);
					void alertServerError(error)
					return [ajaxStatusErrorAction({name: sisName, ts, cause: parseServerError(error)})]
				}),
			),
		)
	}),
)


export const BSCollectionFetcherEpic = combineEpics(
	RefreshBSCollectionEpic,
	FetchBSCollectionEpic,
)